<template>
  <div class="fin-drawer-backdrop" @click.self="$emit('close')">
    <div class="fin-drawer">
      <div class="fin-drawer-head">
        <div>
          <div class="fin-drawer-title">{{ product.name }}</div>
          <div class="fin-drawer-sub">{{ product.code }}<span v-if="product.barcode"> · {{ product.barcode }}</span></div>
        </div>
        <button type="button" class="fin-btn" @click="$emit('close')"><i class="fa fa-times"></i> Mbyll</button>
      </div>

      <div class="fin-drawer-body">
        <p class="fin-drawer-section-title">Gjendja aktuale sipas magazinës</p>
        <table class="fin-mini-table">
          <thead>
            <tr>
              <th>Magazina</th>
              <th style="text-align: right">Sasia</th>
              <th style="text-align: right">Vlera</th>
              <th style="text-align: right">Ndryshimi i fundit</th>
            </tr>
          </thead>
          <tbody v-if="currentLoading">
            <tr v-for="n in 4" :key="'sk-cur-' + n" class="fin-skeleton-row">
              <td><span class="fin-skeleton-line fin-skeleton-line--md"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--sm"></span></td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="row in current" :key="row.warehouse_code">
              <td>{{ row.warehouse_name }}</td>
              <td style="text-align: right">{{ formatQty(row.quantity) }}</td>
              <td style="text-align: right">{{ formatValue(row.value) }}</td>
              <td style="text-align: right">{{ formatDateTime(row.last_changed_at) }}</td>
            </tr>
            <tr v-if="!current.length">
              <td colspan="4" class="fin-empty">Financa nuk raporton gjendje për këtë produkt.</td>
            </tr>
          </tbody>
        </table>

        <p class="fin-drawer-section-title">Lëvizjet ({{ rangeLabel }})</p>
        <table class="fin-mini-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Magazina</th>
              <th style="text-align: right">Nga</th>
              <th style="text-align: right">Në</th>
              <th style="text-align: right">Ndryshimi</th>
            </tr>
          </thead>
          <tbody v-if="movementsLoading">
            <tr v-for="n in 6" :key="'sk-mov-' + n" class="fin-skeleton-row">
              <td><span class="fin-skeleton-line fin-skeleton-line--sm"></span></td>
              <td><span class="fin-skeleton-line fin-skeleton-line--md"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
              <td style="text-align: right"><span class="fin-skeleton-line fin-skeleton-line--xs"></span></td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="movement in movements" :key="movement.id">
              <td>{{ formatDateTime(movement.occurred_at) }}</td>
              <td>{{ movement.warehouse_name }}</td>
              <td style="text-align: right">{{ formatQty(movement.qty_before) }}</td>
              <td style="text-align: right">{{ formatQty(movement.qty_after) }}</td>
              <td style="text-align: right" :class="Number(movement.delta) >= 0 ? 'fin-cell-in' : 'fin-cell-out'">
                {{ formatSigned(movement.delta) }}
              </td>
            </tr>
            <tr v-if="!movements.length">
              <td colspan="5" class="fin-empty">Asnjë lëvizje në këtë periudhë.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import FinancaStockService from "@/services/FinancaStock";
import formatMixin from "../shared/formatMixin.js";

export default {
  name: "FinancaProductHistoryDrawer",
  mixins: [formatMixin],
  props: {
    product: { type: Object, required: true },
    filters: { type: Object, default: () => ({}) },
    range: { type: Object, default: null },
  },
  emits: ["close"],
  data() {
    return {
      currentLoading: true,
      movementsLoading: true,
      movements: [],
      current: [],
      loadSeq: 0,
    };
  },
  computed: {
    rangeLabel() {
      if (!this.range) return "30 ditët e fundit";
      return this.formatDateTime(this.range.from) + " - " + this.formatDateTime(this.range.to);
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    basePayload() {
      const payload = {
        product_code: this.product.code,
        pos_ids: this.filters.pos_ids || [],
        warehouse_ids: this.filters.warehouse_ids || [],
      };
      if (this.range) {
        payload.from = this.range.from;
        payload.to = this.range.to;
      } else {
        const to = new Date();
        const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000);
        payload.from = this.toLocalInput(from).replace("T", " ") + ":00";
        payload.to = this.toLocalInput(to).replace("T", " ") + ":00";
      }
      return payload;
    },

    async load() {
      const seq = ++this.loadSeq;
      this.currentLoading = true;
      this.movementsLoading = true;
      this.current = [];
      this.movements = [];

      const payload = this.basePayload();

      const currentPromise = FinancaStockService.getProductHistoryCurrent(payload)
        .then((data) => {
          if (seq !== this.loadSeq) return;
          this.current = data.current || [];
        })
        .catch(() => {
          if (seq !== this.loadSeq) return;
          this.current = [];
        })
        .finally(() => {
          if (seq === this.loadSeq) this.currentLoading = false;
        });

      const movementsPromise = FinancaStockService.getProductHistoryMovements(payload)
        .then((data) => {
          if (seq !== this.loadSeq) return;
          this.movements = data.movements || [];
        })
        .catch(() => {
          if (seq !== this.loadSeq) return;
          this.movements = [];
        })
        .finally(() => {
          if (seq === this.loadSeq) this.movementsLoading = false;
        });

      await Promise.all([currentPromise, movementsPromise]);
    },
  },
};
</script>
